<template>
  <div class="musubi-page musubi-page--pipeline">
    <header class="musubi-page__head">
      <h1>New pipeline</h1>
      <p class="text-muted">
        One guided path from crawl to enrolled lead: choose <strong>where to look</strong>, define
        <strong>who to target</strong>, decide <strong>what happens to matches</strong>, then review and launch.
        Everything here reuses your existing sources, prospects and drip flows — nothing new is sent until you launch.
      </p>
    </header>

    <!-- ── AI prompt panel (usability-refocus §5a): describe → draft → review ──
         Shared component (ui/AiPromptPanel) — identical surface to the Pipelines
         home hero; this component keeps its own generate() flow via @generate. -->
    <AiPromptPanel
      v-model="promptText"
      variant="inline"
      subtitle="We draft it — the source, audience, flow and email — then you review before anything runs."
      :examples="examplePrompts"
      :generating="generating"
      :error="generateError"
      cost-text="A draft uses a few AI tokens (≈ 15–40). Nothing sends — you review the draft first."
      divider-text="or set it up manually below"
      @generate="generate"
    />

    <!-- Stepper -->
    <nav class="pw-steps" aria-label="Pipeline setup steps">
      <button
        v-for="s in steps" :key="s.n" type="button"
        class="pw-step"
        :class="{ 'is-current': step === s.n, 'is-done': stepState(s.n) === 'done', 'is-todo': stepState(s.n) === 'todo' }"
        :disabled="!canJumpTo(s.n)"
        @click="goTo(s.n)"
      >
        <span class="pw-step__num">
          <CygnusIcon name="check" v-if="stepState(s.n) === 'done'" />
          <template v-else>{{ s.n }}</template>
        </span>
        <span class="pw-step__body">
          <span class="pw-step__title">{{ s.title }}</span>
          <span class="pw-step__sub">{{ s.sub }}</span>
        </span>
      </button>
    </nav>

    <div v-if="error" class="alert alert-danger py-2 small">{{ error }}</div>

    <!-- ── Step 1 — Where to look ─────────────────────────────────────────── -->
    <MusubiSectionCard v-show="step === 1" title="Where to look" icon="broadcast" subtitle="Pick a crawl source, or build a new one">
      <div class="pw-mode">
        <label class="pw-mode__opt" :class="{ 'is-on': sourceMode === 'existing' }">
          <input type="radio" value="existing" v-model="sourceMode"> Use an existing source
        </label>
        <label class="pw-mode__opt" :class="{ 'is-on': sourceMode === 'new' }">
          <input type="radio" value="new" v-model="sourceMode"> Create a new source
        </label>
      </div>

      <!-- existing -->
      <div v-if="sourceMode === 'existing'" class="mt-3">
        <div v-if="loadingSources" class="text-muted small py-2">Loading sources…</div>
        <p v-else-if="!sources.length" class="pw-empty">
          You have no crawl sources yet.
          <button type="button" class="btn btn-link btn-sm p-0 align-baseline" @click="sourceMode = 'new'">Create your first one →</button>
        </p>
        <div v-else class="pw-srclist">
          <button
            v-for="src in sources" :key="src.id" type="button"
            class="pw-srcrow" :class="{ 'is-on': selectedSourceId === src.id }"
            @click="selectedSourceId = src.id"
          >
            <span class="pw-srcrow__radio"><CygnusIcon name="checkbox" :variant="selectedSourceId === src.id ? 'solid' : 'light'" /></span>
            <span class="pw-srcrow__body">
              <span class="pw-srcrow__name">{{ src.name || ('#' + src.id) }}</span>
              <span class="pw-srcrow__meta">{{ typeLabel(src.type) }}<span v-if="src.config && src.config.query"> · “{{ src.config.query }}”</span></span>
            </span>
            <MusubiStatusPill :status="src.active ? 'active' : 'paused'" :label="src.active ? 'active' : 'inactive'" />
          </button>
        </div>
      </div>

      <!-- new -->
      <div v-else class="mt-3">
        <p v-if="createdSource" class="alert alert-success py-2 small mb-3">
          <CygnusIcon name="check-circle" class="me-1" />Created source <strong>{{ createdSource.name }}</strong> (#{{ createdSource.id }}). You can continue.
        </p>
        <!-- ── Vertical entry point ────────────────────────────────────────
             The source-type tiles below answer "which crawler?", which is not
             the question an operator has. They have a VERTICAL in mind ("digital
             agencies in Brighton"). This picks the right crawler for that
             vertical and preconfigures it: OpenStreetMap for broad, well-mapped
             physical categories; Google Places for the niches OSM barely knows
             about. The tiles stay fully usable underneath. -->
        <div class="pw-vertical">
          <div class="pw-vertical__h"><CygnusIcon name="pin" class="me-1" />What kind of business are you looking for?</div>
          <input
            v-model="verticalQuery" type="text" class="form-control form-control-sm pw-vertical__in"
            placeholder="e.g. digital agencies, cafes, plumbers, accountants"
            @keydown.enter.prevent="applyFreeTextVertical"
          >
          <div v-if="btLoading" class="text-muted small mt-2">Loading business types…</div>
          <template v-else>
            <div v-if="verticalMatches.length" class="pw-vertical__list">
              <button
                v-for="p in verticalMatches" :key="p.slug" type="button"
                class="pw-vertical__opt" :class="{ 'is-on': chosenVertical && chosenVertical.slug === p.slug }"
                @click="applyVertical(p)"
              >
                <span class="pw-vertical__label">{{ p.label }}</span>
                <span class="pw-vertical__cov" :class="'pw-vertical__cov--' + p.coverage">{{ coverageLabel(p) }}</span>
              </button>
            </div>
            <button
              v-else-if="verticalQuery.trim()" type="button"
              class="btn btn-outline-secondary btn-sm mt-2" @click="applyFreeTextVertical"
            >
              <CygnusIcon name="search" class="me-1" />Search Google Places for “{{ verticalQuery.trim() }}”
            </button>
          </template>
          <p v-if="verticalNote" class="pw-vertical__note">
            <CygnusIcon name="info-circle" class="me-1" />{{ verticalNote }}
            <button type="button" class="btn btn-link btn-sm p-0 ms-1 align-baseline" @click="clearVertical">Start over</button>
          </p>
          <!-- Phase 2 — a map or a Places listing only finds firms that are
               VISIBLE. Where the chosen preset has SIC codes, offer the register
               as well: it reaches the same vertical's companies that have no
               storefront at all. Plan-locked tenants never see the offer. -->
          <p v-if="chSicOffer" class="pw-vertical__note">
            <CygnusIcon name="building" class="me-1" />Also reach registered {{ chSicOffer.label.toLowerCase() }}
            companies with no storefront, using the Companies House register (SIC {{ chSicOffer.sic_codes.slice(0, 3).join(', ') }}).
            <button type="button" class="btn btn-link btn-sm p-0 ms-1 align-baseline" @click="applyChSic(chSicOffer)">Use Companies House</button>
          </p>
          <p v-else class="pw-vertical__hint">
            Or skip this and pick a crawler yourself below.
          </p>
        </div>

        <CrawlSourceFields
          :key="crawlSeed"
          :api="api"
          :initial-type="crawlInitialType"
          :initial-config="crawlInitialConfig"
          :initial-name="crawlInitialName"
          @change="onCrawlChange"
        />
        <div v-if="createError" class="alert alert-warning py-2 small mt-2 mb-0">{{ createError }}</div>
      </div>

      <template #actions><span class="pw-hint small">Step 1 of 4</span></template>

      <div class="pw-nav">
        <span></span>
        <button class="btn btn-primary" :disabled="!step1Ready || creatingSource" @click="advanceFromStep1">
          <CygnusIcon name="spinner" :spin="true" class="me-1" v-if="creatingSource" />
          {{ sourceMode === 'new' && !createdSource ? 'Create source & continue' : 'Continue' }}
          <CygnusIcon name="arrow-right" class="ms-1" v-if="!creatingSource" />
        </button>
      </div>
    </MusubiSectionCard>

    <!-- ── Step 2 — Who to target ─────────────────────────────────────────── -->
    <MusubiSectionCard v-show="step === 2" title="Who to target" icon="pin" subtitle="Define the audience these matches must fit">
      <div class="pw-aud">
        <!-- ── AI "Suggest targeting" (usability-refocus §5b) — SCOPED: fills this
             step only. Teal = AI. One free-text box → grounded chips → Apply. -->
        <div class="pw-suggest">
          <div class="pw-suggest__h"><CygnusIcon name="bolt" /> Suggest targeting</div>
          <div class="pw-suggest__in">
            <input
              v-model="suggestText" type="text" class="pw-suggest__fld"
              placeholder="e.g. wellness studios in Bath with a few staff"
              :disabled="suggestBusy" @keydown.enter.prevent="runSuggest"
            >
            <button type="button" class="btn-ai pw-suggest__go" :disabled="suggestBusy || !suggestText.trim()" @click="runSuggest">
              <CygnusIcon name="spinner" :spin="true" class="me-1" v-if="suggestBusy" />
              <CygnusIcon name="bolt" class="me-1" v-else />Suggest
            </button>
          </div>
          <p class="pw-suggest__meta"><CygnusIcon name="info-circle" class="me-1" />Fills this step only — you can still edit everything. Uses 1 AI token.</p>

          <div v-if="suggestError" class="alert alert-warning py-1 px-2 small mt-2 mb-0">{{ suggestError }}</div>

          <div v-if="suggestResult" class="pw-suggest__out">
            <div class="pw-suggest__ol"><CygnusIcon name="bolt" /> Suggested — review, then apply</div>
            <div v-if="suggestHasChips" class="pw-suggest__chips">
              <span v-for="(lbl, i) in suggestResult.sector_labels" :key="'sc' + i" class="pw-suggest__chip">
                <span class="pw-suggest__code">{{ suggestResult.sectors[i] }}</span>{{ lbl }}
              </span>
              <span v-for="(r, i) in suggestResult.region" :key="'rg' + i" class="pw-suggest__chip pw-suggest__chip--geo">
                <span class="pw-suggest__code">{{ r }}</span>region
              </span>
              <span v-if="Number(suggestResult.min_employees) > 0" class="pw-suggest__chip pw-suggest__chip--geo">staff ≥ {{ suggestResult.min_employees }}</span>
            </div>
            <p v-else class="pw-suggest__none">Couldn’t ground that into a target — try naming an industry and a place (e.g. “accountants in Leeds”).</p>
            <div v-if="suggestHasChips" class="pw-suggest__foot">
              <span class="pw-suggest__est">≈ {{ (suggestResult.match_estimate || 0).toLocaleString() }} match today</span>
              <button type="button" class="btn btn-primary btn-sm" @click="applySuggestion">Apply to this step</button>
            </div>
          </div>
        </div>

        <div class="pw-aud__row">
          <label class="pw-aud__lbl">Industry</label>
          <MusubiSectorSelect v-model="audience.sectors" placeholder="Any industry" class="pw-aud__ctrl" />
        </div>
        <div class="pw-aud__grid">
          <div>
            <label class="pw-aud__lbl">Minimum staff</label>
            <input v-model.number="audience.min_employees" type="number" min="0" class="form-control form-control-sm" placeholder="e.g. 10">
            <span class="pw-aud__note">Companies House headcount ≥ this</span>
          </div>
          <div>
            <label class="pw-aud__lbl">Region</label>
            <input v-model="audience.region" type="text" list="pw-dl-regions" class="form-control form-control-sm" placeholder="Any region">
            <datalist id="pw-dl-regions"><option v-for="r in regions" :key="r" :value="r"></option></datalist>
          </div>
          <div>
            <label class="pw-aud__lbl">Company status</label>
            <select v-model="audience.company_status" class="form-select form-select-sm">
              <option value="">Any status</option>
              <option value="active">active</option>
              <option value="dissolved">dissolved</option>
              <option value="liquidation">liquidation</option>
              <option value="administration">administration</option>
            </select>
            <p class="pw-aud__hint">Dissolved and inactive companies are left out unless you ask for them.</p>
          </div>
        </div>

        <details class="pw-advanced mt-1">
          <summary>Advanced — temperature &amp; stage</summary>
          <div class="pw-aud__grid mt-2">
            <div>
              <label class="pw-aud__lbl">Temperature</label>
              <select v-model="audience.temperature" class="form-select form-select-sm">
                <option value="">Any temperature</option>
                <option value="warm">warm</option>
                <option value="cold">cold</option>
              </select>
            </div>
            <div>
              <label class="pw-aud__lbl">Stage</label>
              <select v-model="audience.stage" class="form-select form-select-sm">
                <option value="">Any stage</option>
                <option value="new">new</option>
                <option value="qualifying">qualifying</option>
                <option value="qualified">qualified</option>
                <option value="promoted">promoted</option>
              </select>
            </div>
          </div>
        </details>

        <div class="pw-preview" :class="{ 'is-empty': !hasAudienceFilters }">
          <CygnusIcon name="users" />
          <span v-if="previewBusy" class="pw-preview__n">…</span>
          <template v-else-if="previewCount !== null">
            <span class="pw-preview__n">≈ {{ previewCount.toLocaleString() }}</span>
            <span class="pw-preview__t">{{ hasAudienceFilters ? 'prospects match these filters today' : 'total prospects (no filters yet)' }}</span>
          </template>
          <span v-else class="pw-preview__t text-muted">Adjust a filter to see how many prospects match.</span>
        </div>
        <p v-if="previewError" class="text-muted small mb-0">{{ previewError }}</p>
      </div>

      <template #actions><span class="pw-hint small">Step 2 of 4</span></template>

      <div class="pw-nav">
        <button class="btn btn-link" @click="goTo(1)"><CygnusIcon name="arrow-left" class="me-1" />Back</button>
        <button class="btn btn-primary" @click="goTo(3)">Continue<CygnusIcon name="arrow-right" class="ms-1" /></button>
      </div>
    </MusubiSectionCard>

    <!-- ── Step 3 — What happens to matches ───────────────────────────────── -->
    <MusubiSectionCard v-show="step === 3" title="What happens to matches" icon="envelope" subtitle="Enrol them into a drip flow">
      <!-- Role-degraded state: this user has ACQUIRE but not OUTREACH, so the
           drip-flows API 403s. Be honest about it instead of showing an empty
           "no flows" state that then fails opaquely at launch. -->
      <div v-if="flowsForbidden" class="pw-forbidden alert alert-warning py-3 small mb-0">
        <CygnusIcon name="lock" class="me-2" />
        <span>
          <strong>Your role can’t manage drip flows (needs Outreach access).</strong>
          You can set up the source and audience above, but attaching to or creating a flow —
          and launching this pipeline — needs an operator with Outreach permission.
          Ask one to finish this step, or hand off the source &amp; audience you’ve defined.
        </span>
      </div>

      <template v-else>
      <!-- ── Pipeline mode: reach out vs. just discover ─────────────────────
           Discovery mode builds the database without ever contacting anyone.
           It maps to a drip flow created in the 'discovering' state (fail-closed:
           the runner never enrols or sends a discovering flow). -->
      <div class="pw-modechoice">
        <button
          type="button" class="pw-modecard" :class="{ 'is-on': pipelineMode === 'outreach' }"
          @click="pipelineMode = 'outreach'"
        >
          <span class="pw-modecard__radio"><CygnusIcon name="checkbox" :variant="pipelineMode === 'outreach' ? 'solid' : 'light'" /></span>
          <span class="pw-modecard__body">
            <span class="pw-modecard__title"><CygnusIcon name="envelope" class="me-1" />Find &amp; reach out</span>
            <span class="pw-modecard__sub">Crawl for matches and enrol them into an email flow.</span>
          </span>
        </button>
        <button
          type="button" class="pw-modecard" :class="{ 'is-on': pipelineMode === 'discovery' }"
          @click="pipelineMode = 'discovery'"
        >
          <span class="pw-modecard__radio"><CygnusIcon name="checkbox" :variant="pipelineMode === 'discovery' ? 'solid' : 'light'" /></span>
          <span class="pw-modecard__body">
            <span class="pw-modecard__title"><CygnusIcon name="search" class="me-1" />Just discover - build my database, don't contact anyone</span>
            <span class="pw-modecard__sub">Crawls keep running and matches pile up. Nobody is ever emailed.</span>
          </span>
        </button>
      </div>

      <!-- Discovery mode: no outreach config - a name, and what will happen. -->
      <div v-if="pipelineMode === 'discovery'" class="mt-3">
        <ul class="pw-bullets">
          <li><CygnusIcon name="broadcast" />The crawl source you picked keeps running and pulls in businesses as it finds them.</li>
          <li><CygnusIcon name="users" />Matches accumulate under <a href="/acquire/prospects">Prospects</a> and Companies - nothing is enrolled.</li>
          <li><CygnusIcon name="lock" />Nothing is ever emailed while this pipeline is in discovery.</li>
          <li><CygnusIcon name="rocket" />You can turn on outreach later - go live from the Drip flows page whenever you're ready.</li>
        </ul>
        <div class="pw-aud__row mt-3">
          <label class="pw-aud__lbl">Name this pipeline</label>
          <input v-model="newFlow.name" type="text" class="form-control form-control-sm pw-aud__ctrl" :placeholder="defaultDiscoveryName()">
          <span class="pw-aud__note">Optional - we'll name it after your audience if you leave it blank.</span>
        </div>
      </div>

      <!-- Outreach mode: attach or create a drip flow. -->
      <template v-else>
      <div class="pw-mode">
        <label class="pw-mode__opt" :class="{ 'is-on': flowMode === 'existing' }">
          <input type="radio" value="existing" v-model="flowMode"> Attach an active flow
        </label>
        <label class="pw-mode__opt" :class="{ 'is-on': flowMode === 'new' }">
          <input type="radio" value="new" v-model="flowMode"> Create a new flow
        </label>
      </div>

      <!-- existing active flows -->
      <div v-if="flowMode === 'existing'" class="mt-3">
        <div v-if="loadingFlows" class="text-muted small py-2">Loading flows…</div>
        <p v-else-if="!activeFlows.length" class="pw-empty">
          No active flows yet.
          <button type="button" class="btn btn-link btn-sm p-0 align-baseline" @click="flowMode = 'new'">Create one now →</button>
        </p>
        <div v-else class="pw-srclist">
          <button
            v-for="f in activeFlows" :key="f.id" type="button"
            class="pw-srcrow" :class="{ 'is-on': selectedFlowId === f.id }"
            @click="selectedFlowId = f.id"
          >
            <span class="pw-srcrow__radio"><CygnusIcon name="checkbox" :variant="selectedFlowId === f.id ? 'solid' : 'light'" /></span>
            <span class="pw-srcrow__body">
              <span class="pw-srcrow__name">{{ f.name }}</span>
              <span class="pw-srcrow__meta">trigger: {{ f.trigger_type }}</span>
            </span>
            <MusubiStatusPill status="active" />
          </button>
        </div>
        <p class="pw-aud__note mt-2">Your Step 2 audience is added to this flow as an extra auto-enrol rule — its existing audience is untouched.</p>
      </div>

      <!-- new inline flow -->
      <div v-else class="mt-3">
        <div class="pw-aud__row">
          <label class="pw-aud__lbl">Flow name</label>
          <input v-model="newFlow.name" type="text" class="form-control form-control-sm pw-aud__ctrl" placeholder="e.g. Local agencies — intro sequence">
        </div>
        <div class="pw-aud__grid">
          <div style="grid-column: 1 / -1;">
            <label class="pw-aud__lbl">First email template</label>

            <div v-if="loadingTemplates" class="text-muted small py-2">Loading templates…</div>

            <!-- Picker (templates exist and we're not mid-create) -->
            <template v-else-if="emailTemplates.length && !showTemplateCreate">
              <div class="pw-tplpick">
                <select v-model="newFlow.template_id" class="form-select form-select-sm">
                  <option :value="null" disabled>Select an email template…</option>
                  <option v-for="t in emailTemplates" :key="t.id" :value="t.id">{{ t.title || ('#' + t.id) }}</option>
                </select>
                <button type="button" class="btn btn-outline-secondary btn-sm pw-tplpick__new" @click="openTemplateCreate">
                  <CygnusIcon name="plus" class="me-1" />New template
                </button>
              </div>
              <span class="pw-aud__note">Step 1 of the flow sends this email. Its subject is the template name.</span>
            </template>

            <!-- Inline create panel — no route change, so the wizard state survives. -->
            <div v-if="showTemplateCreate" class="pw-tplcreate">
              <div class="pw-tplcreate__h"><CygnusIcon name="envelope" class="me-1" />New email template</div>
              <p v-if="!emailTemplates.length" class="pw-tplcreate__empty">
                No templates yet - create your first one here, without leaving this wizard.
              </p>
              <div class="pw-aud__row">
                <label class="pw-aud__lbl">Template name &amp; subject line</label>
                <input
                  v-model="newTemplate.title" type="text" class="form-control form-control-sm"
                  placeholder="e.g. Intro - a quick hello" :disabled="creatingTemplate"
                  @keydown.enter.prevent="createTemplateInline">
                <span class="pw-aud__note">This is the email's subject line and how the template shows in your list.</span>
              </div>
              <div class="pw-aud__row mt-2">
                <label class="pw-aud__lbl">Body</label>
                <textarea
                  v-model="newTemplate.body" rows="4" class="form-control form-control-sm"
                  placeholder="Hi {contact.first_name}, I came across {contact.company_name} and…"
                  :disabled="creatingTemplate"></textarea>
                <span class="pw-aud__note">A simple first draft. Add blocks, images and merge tokens later in the full editor.</span>
              </div>
              <div v-if="templateCreateError" class="alert alert-warning py-1 px-2 small mt-2 mb-0">{{ templateCreateError }}</div>
              <div class="pw-tplcreate__foot">
                <a href="/admin/email-templates" target="_blank" rel="noopener" class="pw-tplcreate__link">
                  <CygnusIcon name="external-link" class="me-1" />Open the full editor in a new tab
                </a>
                <div class="pw-tplcreate__actions">
                  <button
                    v-if="emailTemplates.length" type="button" class="btn btn-link btn-sm"
                    :disabled="creatingTemplate" @click="cancelTemplateCreate">Cancel</button>
                  <button
                    type="button" class="btn btn-primary btn-sm"
                    :disabled="creatingTemplate || !newTemplate.title.trim()" @click="createTemplateInline">
                    <CygnusIcon name="spinner" :spin="true" class="me-1" v-if="creatingTemplate" />Create &amp; use
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div>
            <label class="pw-aud__lbl">Then wait</label>
            <div class="pw-wait">
              <input v-model.number="newFlow.wait_value" type="number" min="1" class="form-control form-control-sm">
              <select v-model.number="newFlow.wait_unit" class="form-select form-select-sm">
                <option :value="3600">hours</option>
                <option :value="86400">days</option>
              </select>
            </div>
            <span class="pw-aud__note">A pause before the next step you add later.</span>
          </div>
        </div>
        <p class="pw-aud__note mt-1">
          <CygnusIcon name="info-circle" class="me-1" />Created paused as a draft. It only starts enrolling once you Launch (or activate it on the Drip flows page). No email sends until a runner tick.
        </p>
      </div>
      </template>
      </template>

      <template #actions><span class="pw-hint small">Step 3 of 4</span></template>

      <div class="pw-nav">
        <button class="btn btn-link" @click="goTo(2)"><CygnusIcon name="arrow-left" class="me-1" />Back</button>
        <button class="btn btn-primary" :disabled="!step3Ready" @click="goToReview">Review<CygnusIcon name="arrow-right" class="ms-1" /></button>
      </div>
    </MusubiSectionCard>

    <!-- ── Step 4 — Review & launch ───────────────────────────────────────── -->
    <MusubiSectionCard v-show="step === 4" title="Review &amp; launch" icon="rocket" subtitle="Confirm the pipeline">
      <div class="pw-summary">
        <CygnusIcon name="diagram" class="pw-summary__icon" />
        <p class="pw-summary__text">{{ summarySentence }}</p>
      </div>

      <ul class="pw-review">
        <li>
          <span class="pw-review__k"><CygnusIcon name="broadcast" />Source</span>
          <span class="pw-review__v">{{ sourceSummary }}</span>
        </li>
        <li>
          <span class="pw-review__k"><CygnusIcon name="pin" />Audience</span>
          <span class="pw-review__v">{{ audienceSummary }}<span v-if="previewCount !== null" class="text-muted"> · ≈ {{ previewCount.toLocaleString() }} match today</span></span>
        </li>
        <li>
          <span class="pw-review__k"><CygnusIcon name="envelope" />Flow</span>
          <span class="pw-review__v">{{ flowSummary }}</span>
        </li>
      </ul>

      <div v-if="launchResult" class="alert alert-success py-2 small">{{ launchResult }}</div>
      <div v-if="launchError" class="alert alert-danger py-2 small">{{ launchError }}</div>

      <template #actions><span class="pw-hint small">Step 4 of 4</span></template>

      <div class="pw-nav pw-nav--launch">
        <button class="btn btn-link" @click="goTo(3)" :disabled="launching"><CygnusIcon name="arrow-left" class="me-1" />Back</button>
        <div class="pw-nav__right">
          <button class="btn btn-outline-secondary" :disabled="launching || launched" @click="run(false)">
            Save without launching
          </button>
          <button class="btn btn-primary" :disabled="launching || launched" @click="run(true)">
            <CygnusIcon name="spinner" :spin="true" class="me-1" v-if="launching" />
            <CygnusIcon :name="pipelineMode === 'discovery' ? 'search' : 'rocket'" class="me-1" v-else />{{ launchLabel }}
          </button>
        </div>
      </div>

      <!-- Finish step — point the user straight at the pipeline they just built.
           The wizard's output is a DRIP FLOW that lands under Drip flows in draft
           status, so we deep-link to it and say where it lives (owner couldn't
           find it before). -->
      <div v-if="launched" class="pw-finish mt-3">
        <div class="pw-finish__head">
          <CygnusIcon name="check-circle" class="pw-finish__ic" />
          <div class="pw-finish__copy">
            <div class="pw-finish__title">Your pipeline{{ createdFlowName ? ` &ldquo;${createdFlowName}&rdquo;` : '' }} is ready</div>
            <div class="pw-finish__sub">
              Review and activate it to start {{ pipelineMode === 'discovery' ? 'gathering matches' : 'sending' }}.
              You'll find it under <strong>Drip flows</strong>.
            </div>
          </div>
        </div>
        <div class="pw-finish__actions">
          <a class="btn btn-primary btn-sm" :href="finishFlowLink">
            <CygnusIcon name="arrow-right" class="me-1" />Review &amp; activate this pipeline
          </a>
          <a class="btn btn-link btn-sm" href="/outreach/drip-flows"><CygnusIcon name="external-link" class="me-1" />All drip flows</a>
          <a class="btn btn-link btn-sm" href="/acquire/prospects"><CygnusIcon name="users" class="me-1" />Prospects</a>
        </div>
      </div>
    </MusubiSectionCard>
  </div>
</template>

<script>
/**
 * PipelineWizard — the usability "front door" over the acquisition machinery.
 * Four steps: (1) pick/create a crawl SOURCE, (2) define an AUDIENCE, (3) attach
 * or create a drip FLOW, (4) review & launch. It invents no backend features —
 * it composes existing endpoints:
 *   - crawl sources : GET/POST /api/v1/admin/crawl/sources
 *   - audience count: GET /api/v1/admin/leads/stats  (same filters as Prospects)
 *   - drip flows    : GET/POST/PUT /api/v1/admin/drip-flows, /{id}/activate,
 *                     /audiences/sync
 * The Step-2 audience is written into the flow's audience_json (an OR-list of
 * filter sets) — that is the "watcher". Launch activates a newly-created flow
 * and triggers one audience sync; sends still require a separate runner tick,
 * which this wizard never fires.
 */
import useApi from "@apexl/icarus-core/src/services/api";
import { apiErrorMessage, isAiUnavailable } from "./util/aiError";
import AiPromptPanel from "@root/components/ui/AiPromptPanel.vue";
import MusubiSectionCard from "@root/components/ui/MusubiSectionCard.vue";
import MusubiStatusPill from "@root/components/ui/MusubiStatusPill.vue";
import MusubiSectorSelect from "@root/components/MusubiSectorSelect.vue";
import CrawlSourceFields from "@root/components/CrawlSourceFields.vue";
import {
  SOURCE_TYPES, CATEGORY_OPTIONS, crawlTypeLock,
  setBusinessTypeCatalogue, businessTypeCatalogue, businessTypeBySlug,
} from "./util/crawlSourceForm";

/**
 * What a NEW pipeline targets before the operator touches anything. Trading
 * companies only - a dissolved firm is not a lead. Changing this changes only
 * what a fresh wizard proposes; every saved audience is read back verbatim.
 */
const DEFAULT_COMPANY_STATUS = 'active';

export default {
  name: 'PipelineWizard',
  components: { AiPromptPanel, MusubiSectionCard, MusubiStatusPill, MusubiSectorSelect, CrawlSourceFields },
  props: { componentData: { type: Object, default: () => ({}) } },
  data() {
    return {
      api: useApi(),
      step: 1,
      error: null,

      // AI prompt panel (generic service-seller examples only — never decay signals)
      promptText: '',
      generating: false,
      generateError: null,
      // Set when the API answers 503 ai_unavailable — lets the notice read as a
      // temporary outage rather than a failure the operator caused.
      aiDown: false,
      examplePrompts: [
        'IT companies in Gloucestershire',
        'Cafés near canals with no booking system',
        'Accountants in Leeds, 5+ staff',
      ],
      steps: [
        { n: 1, title: 'Where to look', sub: 'Crawl source' },
        { n: 2, title: 'Who to target', sub: 'Audience' },
        { n: 3, title: 'What happens', sub: 'Drip flow' },
        { n: 4, title: 'Review', sub: 'Launch' },
      ],

      // step 1 — source
      sourceMode: 'existing',
      sources: [],
      loadingSources: false,
      selectedSourceId: null,
      presetSourceId: null,
      crawlPayload: { type: 'local_search', name: '', config: {}, valid: false },
      // step 1 — vertical entry point (steering, not a rewrite): a business type
      // chosen here preconfigures the right crawler; the tiles stay usable.
      btCatalogue: [],
      btLoading: false,
      verticalQuery: '',
      chosenVertical: null,
      verticalNote: null,
      // A suggestion chip on the home page deep-links here with the vertical AND
      // the town already decided (?bt=<preset slug>|?q=<free text>&location=<town>).
      // presetVertical holds that until the catalogue + plan gates have loaded;
      // verticalLocation is what every seeded config's `location` is filled with.
      presetVertical: null,
      verticalLocation: '',
      crawlTypes: {},               // /me/usage crawl-type gate map (plan locks)
      crawlInitialType: 'local_search',
      crawlInitialConfig: null,
      crawlInitialName: '',
      crawlSeed: 0,                 // bump to remount CrawlSourceFields on a new preset
      creatingSource: false,
      createError: null,
      createdSource: null,

      // step 2 — audience
      // A NEW pipeline targets trading companies. Dissolved / liquidation /
      // administration are opt-ins, not a default you have to remember to
      // exclude. Saved pipelines are untouched: nothing reads a default on the
      // way back in (DripAudienceService, AudienceFilterMapper and TriggerMatcher
      // all treat an absent company_status as "any"), so an existing audience
      // keeps matching exactly what it matched yesterday.
      audience: { sectors: [], min_employees: '', region: '', company_status: DEFAULT_COMPANY_STATUS, temperature: '', stage: '' },
      sectorLabels: {},
      regions: [],
      previewCount: null,
      previewBusy: false,
      previewError: null,
      previewTimer: null,

      // step 2 — AI "Suggest targeting" (§5b, scoped: fills this step only)
      suggestText: '',
      suggestBusy: false,
      suggestError: null,
      suggestResult: null,

      // step 3 — what happens to matches
      // 'outreach' = find & enrol into a drip flow (default). 'discovery' = just
      // build the database; the pipeline is created 'discovering' and never sends.
      pipelineMode: 'outreach',
      // step 3 — flow (outreach mode only)
      flowMode: 'existing',
      flows: [],
      loadingFlows: false,
      // True when GET /drip-flows 403s — i.e. this role has ACQUIRE but not
      // OUTREACH. Step 3 then degrades to an honest "needs Outreach" notice.
      flowsForbidden: false,
      selectedFlowId: null,
      newFlow: { name: '', template_id: null, wait_value: 3, wait_unit: 86400 },

      // step 3 — email templates. The "create a new flow" path needs a first
      // email template; a tenant with none used to hit a dead-end empty select.
      // We own the picker (rather than MusubiEntitySelect) so we can reveal an
      // inline creator, then refresh + auto-select without leaving the wizard.
      emailTemplates: [],
      loadingTemplates: false,
      templatesLoaded: false,
      showTemplateCreate: false,
      creatingTemplate: false,
      templateCreateError: null,
      newTemplate: { title: '', body: '' },

      // step 4 — launch
      launching: false,
      launched: false,
      launchResult: null,
      launchError: null,
      // The flow this wizard just created/attached — drives the finish deep-link
      // ("Review & activate this pipeline" → /outreach/drip-flows?flow={id}, which
      // the Drip flows list honours by opening that flow's editor directly).
      createdFlowId: null,
      createdFlowName: '',
    };
  },
  computed: {
    activeFlows() {
      return this.flows.filter((f) => f.status === 'active');
    },
    step1Ready() {
      if (this.sourceMode === 'existing') return !!this.selectedSourceId;
      return !!this.createdSource || !!this.crawlPayload.valid;
    },
    step3Ready() {
      // No Outreach access → can't attach or create a flow, so Step 3 can't be
      // completed (the Review button stays disabled). The drips API is Outreach-
      // gated end to end, so this also blocks creating a discovery pipeline.
      if (this.flowsForbidden) return false;
      // Discovery mode needs no flow config — source (step 1) + audience (step 2)
      // are enough; the name is optional (auto-derived when blank).
      if (this.pipelineMode === 'discovery') return true;
      if (this.flowMode === 'existing') return !!this.selectedFlowId;
      return !!(this.newFlow.name && this.newFlow.name.trim()) && !!this.newFlow.template_id;
    },
    hasAudienceFilters() {
      const a = this.audience;
      return !!(a.sectors.length || Number(a.min_employees) > 0 || a.region.trim() || a.company_status || a.temperature || a.stage);
    },
    /**
     * As above, but ignoring the ACTIVE default. The preview strapline SHOULD
     * count it (the count really is filtered), whereas the auto-generated
     * pipeline name should not: "Discovery - all industries, active" says less
     * about an untouched form than the source's own name does.
     */
    hasNarrowedAudience() {
      const a = this.audience;
      return !!(
        a.sectors.length || Number(a.min_employees) > 0 || a.region.trim() || a.temperature || a.stage
        || (a.company_status && a.company_status !== DEFAULT_COMPANY_STATUS)
      );
    },
    /** Presets matching what's typed (empty box = the whole catalogue). */
    verticalMatches() {
      const q = this.verticalQuery.trim().toLowerCase();
      if (!q) return this.btCatalogue;
      return this.btCatalogue.filter(
        (p) => p.label.toLowerCase().includes(q)
          || p.slug.includes(q)
          || String(p.places_query || '').toLowerCase().includes(q),
      );
    },
    /**
     * The picked preset, when a Companies House sweep is ALSO worth offering for
     * it: a real catalogue preset (not a free-text vertical), carrying SIC codes,
     * with ch_sic on the plan, and not already the crawler we steered them to.
     */
    chSicOffer() {
      const p = this.chosenVertical;
      if (!p || !p.slug || !Array.isArray(p.sic_codes) || !p.sic_codes.length) return null;
      if (this.crawlInitialType === 'ch_sic') return null;
      if (crawlTypeLock(this.crawlTypes, 'ch_sic').locked) return null;
      return p;
    },
    suggestHasChips() {
      const r = this.suggestResult;
      if (!r) return false;
      return !!((Array.isArray(r.sectors) && r.sectors.length)
        || (Array.isArray(r.region) && r.region.length)
        || Number(r.min_employees) > 0);
    },
    resolvedSource() {
      if (this.sourceMode === 'existing') return this.sources.find((s) => s.id === this.selectedSourceId) || null;
      return this.createdSource || (this.crawlPayload.name
        ? { name: this.crawlPayload.name, type: this.crawlPayload.type }
        : null);
    },
    sourceSummary() {
      const s = this.resolvedSource;
      if (!s) return '—';
      return `${s.name || ('#' + s.id)} · ${this.typeLabel(s.type)}`;
    },
    industryLabel() {
      const toks = this.audience.sectors;
      if (!toks.length) return 'all industries';
      const labels = toks.map((t) => this.shortSectorLabel(t));
      if (labels.length <= 2) return labels.join(' & ');
      return `${labels.slice(0, 2).join(', ')} +${labels.length - 2} more`;
    },
    audienceSummary() {
      const parts = [this.industryLabel];
      if (Number(this.audience.min_employees) > 0) parts.push(`${this.audience.min_employees}+ staff`);
      if (this.audience.region.trim()) parts.push(`in ${this.audience.region.trim()}`);
      if (this.audience.company_status) parts.push(this.audience.company_status);
      if (this.audience.temperature) parts.push(this.audience.temperature);
      if (this.audience.stage) parts.push(this.audience.stage);
      return parts.join(', ');
    },
    flowSummary() {
      if (this.pipelineMode === 'discovery') {
        return 'Discovery only - matches accumulate, nobody is contacted';
      }
      if (this.flowMode === 'existing') {
        const f = this.flows.find((x) => x.id === this.selectedFlowId);
        return f ? `${f.name} (active)` : '—';
      }
      const unit = this.newFlow.wait_unit === 3600 ? 'hour' : 'day';
      const n = this.newFlow.wait_value || 1;
      return `${this.newFlow.name || '(unnamed flow)'} — send template, wait ${n} ${unit}${n === 1 ? '' : 's'} (new, paused)`;
    },
    summarySentence() {
      const src = this.resolvedSource;
      const srcName = src ? (src.name || ('#' + src.id)) : 'your source';
      const staff = Number(this.audience.min_employees) > 0 ? `${this.audience.min_employees}+ staff` : 'any size';
      const region = this.audience.region.trim() ? ` in ${this.audience.region.trim()}` : '';
      if (this.pipelineMode === 'discovery') {
        return `Crawl ${srcName} → ${this.industryLabel}, ${staff}${region} → keep discovering matches into your database. Nobody is emailed.`;
      }
      const flow = this.flowMode === 'existing'
        ? (this.flows.find((x) => x.id === this.selectedFlowId)?.name || 'the flow')
        : (this.newFlow.name || 'a new flow');
      return `Crawl ${srcName} → ${this.industryLabel}, ${staff}${region} → auto-enrol matches into “${flow}”.`;
    },
    /** Primary review-button label — outreach launches, discovery starts gathering. */
    launchLabel() {
      return this.pipelineMode === 'discovery' ? 'Start discovering' : 'Launch pipeline';
    },
    /** Deep-link to the pipeline this wizard just created — the Drip flows list
     *  opens that flow's editor directly when it sees ?flow={id}. Falls back to
     *  the plain list if we somehow finished without an id. */
    finishFlowLink() {
      return this.createdFlowId
        ? `/outreach/drip-flows?flow=${this.createdFlowId}`
        : '/outreach/drip-flows';
    },
  },
  watch: {
    sourceMode() { this.error = null; },
    flowMode(v) { this.error = null; if (v === 'new' && this.pipelineMode === 'outreach') this.ensureTemplatesLoaded(); },
    pipelineMode(v) { this.error = null; if (v === 'outreach' && this.flowMode === 'new') this.ensureTemplatesLoaded(); },
    audience: {
      deep: true,
      handler() { if (this.step === 2) this.schedulePreview(); },
    },
  },
  mounted() {
    // Deep-link from "Next: define who to target →" on the Crawl-sources page:
    // ?source={id} preselects that source and jumps straight to Step 2.
    try {
      const qs = new URLSearchParams(window.location.search);
      const raw = qs.get('source');
      const id = raw ? Number(raw) : null;
      if (id && Number.isFinite(id)) this.presetSourceId = id;

      // Deep-link from a tailored suggestion chip on the home page:
      // ?bt={preset slug} (or ?q={free text}) + &location={town}.
      const slug = (qs.get('bt') || '').trim();
      const free = (qs.get('q') || '').trim();
      const loc = (qs.get('location') || '').trim().slice(0, 120);
      if (slug || free) this.presetVertical = { slug, query: free, location: loc };
    } catch (e) { /* no query params — start clean */ }
    this.loadSources();
    this.loadFlows();
    this.loadSectorCatalogue();
    this.loadRegions();
    // Both must be in before a preset can be applied: the catalogue to resolve
    // the slug, the plan gates so we never steer into a locked crawler.
    Promise.all([this.loadBusinessTypes(), this.loadCrawlTypes()])
      .then(() => this.applyPresetVertical());
  },
  beforeUnmount() {
    if (this.previewTimer) clearTimeout(this.previewTimer);
  },
  methods: {
    /** AI path: POST the brief → generate a PipelineDraft → open its review page. */
    async generate() {
      const prompt = this.promptText.trim();
      if (!prompt || this.generating) return;
      this.generating = true; this.generateError = null;
      try {
        const res = await this.api.post('/api/v1/admin/drafts', { prompt });
        const p = res?.data ?? res;
        const id = p?.draft?.id;
        if (!id) throw new Error('The generator returned no draft.');
        window.location.href = `/acquire/drafts/${id}`;
      } catch (e) {
        const d = e?.response?.data;
        let msg = apiErrorMessage(e, 'Could not generate a draft just now.');
        // An outage spends nothing, so the token note would be a lie there.
        if (!isAiUnavailable(e) && d?.tokens_consumed) msg += ` (${d.tokens_consumed} AI token${d.tokens_consumed === 1 ? '' : 's'} were still used.)`;
        this.generateError = msg;
        this.aiDown = isAiUnavailable(e);
        this.generating = false;
      }
    },
    typeLabel(type) {
      return (SOURCE_TYPES.find((t) => t.value === type) || {}).label || type || '—';
    },

    // ── Vertical entry point ────────────────────────────────────────────────
    /** The shared business-type catalogue (module-cached across components). */
    async loadBusinessTypes() {
      if (businessTypeCatalogue().length) { this.btCatalogue = businessTypeCatalogue(); return; }
      this.btLoading = true;
      try {
        const res = await this.api.get('/api/v1/admin/crawl/business-types');
        const p = res?.data ?? res;
        const list = Array.isArray(p?.data) ? p.data : (Array.isArray(p) ? p : []);
        setBusinessTypeCatalogue(list);
        this.btCatalogue = list;
      } catch (e) {
        this.btCatalogue = []; // fail-open — the tiles below still work
      }
      this.btLoading = false;
    },
    /** Plan gate map, so we never steer someone into a crawler they can't use. */
    async loadCrawlTypes() {
      try {
        const res = await this.api.get('/api/v1/me/usage');
        const p = res?.data ?? res;
        this.crawlTypes = (p && p.crawl_types) || {};
      } catch (e) { /* fail-open — nothing locked */ }
    },
    coverageLabel(preset) {
      if (preset.coverage === 'places') return 'Google Places';
      if (preset.coverage === 'osm') return 'OpenStreetMap';
      return 'Either';
    },
    /**
     * Apply a deep-linked suggestion once the catalogue and the plan gates are
     * in. ?source= wins outright — that link jumps to Step 2 with an existing
     * source, so seeding a new one underneath it would be noise.
     */
    applyPresetVertical() {
      const p = this.presetVertical;
      if (!p || this.presetSourceId) return;
      this.verticalLocation = p.location || '';
      const preset = p.slug ? businessTypeBySlug(p.slug) : null;
      if (preset) { this.applyVertical(preset); return; }
      // An unknown slug is not a dead end: fall back to its free text if the
      // chip carried one, otherwise just prefill the box for the operator.
      const free = (p.query || p.slug || '').trim();
      if (!free) { this.verticalLocation = ''; return; }
      this.verticalQuery = free;
      if (p.query) this.applyFreeTextVertical();
    },
    /** Tail of every steering note: honest about whether a location is still needed. */
    locationHint() {
      return this.verticalLocation
        ? `Location is set to ${this.verticalLocation} - change it below if you want somewhere else.`
        : 'Add a location below.';
    },
    /**
     * Steer + preconfigure. A vertical OSM knows well (cafés, dentists, pubs)
     * goes to the free local_search with that preset's tag selectors. A vertical
     * OSM barely maps (agencies, cleaners, trades) goes to Google Places with the
     * preset's free-text query — that is the whole point of this step. If Places
     * is locked for the plan we say so and fall back to the closest OSM selectors
     * rather than dead-ending.
     */
    applyVertical(preset) {
      const placesLocked = crawlTypeLock(this.crawlTypes, 'google_places').locked;
      const osmLocked    = crawlTypeLock(this.crawlTypes, 'local_search').locked;
      const wantsPlaces  = preset.coverage === 'places';
      this.chosenVertical = preset;
      this.verticalQuery  = preset.label;

      if (wantsPlaces && !placesLocked) {
        this.seedCrawlSource('google_places', {
          query: preset.places_query, location: this.verticalLocation, radius_miles: 10, max: 40,
          auto_enrich: false, schedule: 'manual', rate_per_min: 30,
        }, `${preset.label} (Places)`);
        this.verticalNote = `${preset.label} is rarely mapped on OpenStreetMap, so this is set up as a Google `
          + `Places search - billed per request, but far better coverage. ${this.locationHint()}`;
        return;
      }
      if (wantsPlaces && placesLocked && !osmLocked) {
        this.seedLocalSearch(preset);
        this.verticalNote = `Google Places is not on your plan (${crawlTypeLock(this.crawlTypes, 'google_places').plan || 'a higher plan'} unlocks it), `
          + `so this is a free OpenStreetMap search using the closest tags for ${preset.label}. `
          + 'Expect thinner coverage for this type.';
        return;
      }
      if (osmLocked && !placesLocked) {
        this.seedCrawlSource('google_places', {
          query: preset.places_query, location: this.verticalLocation, radius_miles: 10, max: 40,
          auto_enrich: false, schedule: 'manual', rate_per_min: 30,
        }, `${preset.label} (Places)`);
        this.verticalNote = `This is set up as a Google Places search for ${preset.label} (billed per request). ${this.locationHint()}`;
        return;
      }
      this.seedLocalSearch(preset);
      this.verticalNote = `Set up as a free OpenStreetMap search for ${preset.label}. ${this.locationHint()}`
        + (preset.coverage === 'both' ? ' Or switch to Google Places below for wider coverage.' : '');
    },
    /** local_search preconfigured with a preset's OSM selectors (broad if it has none). */
    seedLocalSearch(preset) {
      const selectors = Array.isArray(preset.osm_selectors) && preset.osm_selectors.length
        ? preset.osm_selectors.slice()
        : [...CATEGORY_OPTIONS];
      const config = {
        location: this.verticalLocation, radius_miles: 10, categories: selectors, max: 100,
        include_no_website: true, auto_enrich: false, schedule: 'manual', rate_per_min: 30,
      };
      if (Array.isArray(preset.osm_selectors) && preset.osm_selectors.length) {
        config.business_type_slugs = [preset.slug];
      }
      this.seedCrawlSource('local_search', config, `${preset.label} (OSM)`);
    },
    /**
     * Phase 2 — search the REGISTER for this vertical instead of a map. Reaches
     * the firms in it that have no storefront, no OSM node and no Places entry;
     * the preset's SIC codes are what the sweep searches on.
     */
    applyChSic(preset) {
      this.chosenVertical = preset;
      this.verticalQuery  = preset.label;
      this.seedCrawlSource('ch_sic', {
        business_types: [preset.slug], location: this.verticalLocation, company_status: 'active',
        max: 200, max_requests: 20, auto_enrich: false, schedule: 'manual', rate_per_min: 30,
      }, `${preset.label} (Companies House)`);
      this.verticalNote = `Set up as a Companies House search for ${preset.label} `
        + `(SIC ${preset.sic_codes.join(', ')}). It finds registered companies whether or not they have a `
        + 'website or a map listing, with their registered name, number and office address already filled in. '
        + this.locationHint();
    },
    /** Nothing in the catalogue matches — that IS the signal to use Places. */
    applyFreeTextVertical() {
      const q = this.verticalQuery.trim();
      if (!q) return;
      const exact = this.btCatalogue.find((p) => p.label.toLowerCase() === q.toLowerCase());
      if (exact) { this.applyVertical(exact); return; }
      if (crawlTypeLock(this.crawlTypes, 'google_places').locked) {
        this.chosenVertical = null;
        this.verticalNote = `No preset matches “${q}”, and Google Places is not on your plan - so there is no `
          + 'good free source for it. Pick a preset above, or build a source yourself below.';
        return;
      }
      this.chosenVertical = { slug: null, label: q, coverage: 'places' };
      this.seedCrawlSource('google_places', {
        query: q, location: this.verticalLocation, radius_miles: 10, max: 40,
        auto_enrich: false, schedule: 'manual', rate_per_min: 30,
      }, `${q} (Places)`);
      this.verticalNote = `No preset matches “${q}”, so this is set up as a Google Places search for it `
        + `(billed per request). ${this.locationHint()}`;
    },
    /** Remount CrawlSourceFields on the chosen crawler + config. */
    seedCrawlSource(type, config, name) {
      this.crawlInitialType   = type;
      this.crawlInitialConfig = config;
      this.crawlInitialName   = name;
      this.createdSource      = null; // a new shape means a new source to create
      this.crawlSeed         += 1;
    },
    clearVertical() {
      this.chosenVertical = null;
      this.verticalNote   = null;
      this.verticalQuery  = '';
      this.verticalLocation = '';
      this.presetVertical = null;
      this.crawlInitialType   = 'local_search';
      this.crawlInitialConfig = null;
      this.crawlInitialName   = '';
      this.createdSource      = null;
      this.crawlSeed         += 1;
    },
    shortSectorLabel(token) {
      const full = this.sectorLabels[token];
      if (!full) return token;
      // "F — Construction" / "41100 — Construction of buildings" → keep the text side.
      const idx = full.indexOf('—');
      return idx === -1 ? full : full.slice(idx + 1).trim();
    },
    stepState(n) {
      if (n === this.step) return 'current';
      if (n < this.step) return 'done';
      return 'todo';
    },
    canJumpTo(n) {
      if (n <= this.step) return true;
      // only allow forward jumps to steps whose prerequisites are met
      if (n >= 2 && !this.step1Ready && !this.createdSource) return false;
      if (n >= 4 && !this.step3Ready) return false;
      return n === this.step + 1;
    },
    goTo(n) {
      if (n === this.step) return;
      this.error = null;
      this.step = n;
      if (n === 2) this.schedulePreview();
    },
    onCrawlChange(payload) {
      this.crawlPayload = payload;
      // If the user edits the form after creating, allow a fresh create.
      if (this.createdSource && (payload.name !== this.createdSource.name)) this.createdSource = null;
    },
    async advanceFromStep1() {
      if (this.sourceMode === 'existing') { this.goTo(2); return; }
      if (this.createdSource) { this.goTo(2); return; }
      await this.createSource();
      if (this.createdSource) this.goTo(2);
    },
    async createSource() {
      this.creatingSource = true; this.createError = null;
      try {
        const res = await this.api.post('/api/v1/admin/crawl/sources', {
          type: this.crawlPayload.type,
          name: this.crawlPayload.name,
          config: this.crawlPayload.config,
        });
        const p = res?.data ?? res;
        const id = p?.id ?? p?.data?.id;
        if (!id) throw new Error('Create returned no id.');
        this.createdSource = { id, name: this.crawlPayload.name, type: this.crawlPayload.type };
        await this.loadSources();
      } catch (e) {
        this.createError = e?.response?.data?.error || e?.message || 'Could not create the source.';
      }
      this.creatingSource = false;
    },
    async loadSources() {
      this.loadingSources = true;
      try {
        const res = await this.api.get('/api/v1/admin/crawl/sources');
        const payload = res?.data?.data ?? res?.data ?? res;
        this.sources = Array.isArray(payload) ? payload : (payload?.rows || []);
        // Apply a ?source={id} deep-link once, if that source exists. Archived
        // sources are not in this list by design, so a link to one lands here -
        // say why rather than dropping the operator on step 1 with no
        // explanation for the selection that did not happen.
        if (this.presetSourceId) {
          if (this.sources.some((s) => s.id === this.presetSourceId)) {
            this.sourceMode = 'existing';
            this.selectedSourceId = this.presetSourceId;
            if (this.step === 1) this.goTo(2);
          } else {
            this.error = `That source is no longer available - it may have been archived. Pick another below, or restore it from Crawl sources.`;
          }
          this.presetSourceId = null;
        }
      } catch (e) { this.error = e?.message || 'Failed to load crawl sources.'; }
      this.loadingSources = false;
    },
    async loadFlows() {
      this.loadingFlows = true;
      try {
        const res = await this.api.get('/api/v1/admin/drip-flows');
        const payload = res?.data?.data ?? res?.data ?? res;
        this.flows = Array.isArray(payload) ? payload : (payload?.rows || []);
      } catch (e) {
        // A 403 means this role lacks OUTREACH — surface it honestly in Step 3
        // rather than showing a misleading empty state that fails at launch.
        // Any other error stays non-fatal (empty state).
        if (e?.response?.status === 403) this.flowsForbidden = true;
      }
      this.loadingFlows = false;
    },
    /** Load the tenant's email templates for the "new flow" picker. OUTREACH-
     *  gated like the rest of step 3 (a 403 is already surfaced by
     *  flowsForbidden, so it stays quiet here). When the tenant has none, open
     *  the inline creator straight away rather than showing an empty select. */
    async ensureTemplatesLoaded(force = false) {
      if (this.templatesLoaded && !force) return;
      this.loadingTemplates = true;
      try {
        const res = await this.api.get('/api/v1/branding/templates');
        const p = res?.data ?? res;
        const rows = Array.isArray(p?.templates) ? p.templates : (p?.data?.templates ?? []);
        this.emailTemplates = (Array.isArray(rows) ? rows : []).filter((t) => t.surface === 'email');
        this.templatesLoaded = true;
      } catch (e) {
        this.emailTemplates = [];
      }
      this.loadingTemplates = false;
      if (!this.emailTemplates.length) this.showTemplateCreate = true;
    },
    openTemplateCreate() { this.templateCreateError = null; this.showTemplateCreate = true; },
    cancelTemplateCreate() { this.showTemplateCreate = false; this.templateCreateError = null; },
    /** Slugify a template name the same way the Templates editor does, but skip
     *  any slug already in use — the create PUT upserts by (surface, slug), so a
     *  name clash would silently overwrite an existing template. */
    templateSlug(title) {
      const base = String(title).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'template';
      const taken = new Set(this.emailTemplates.map((t) => t.slug));
      if (!taken.has(base)) return base;
      let n = 2;
      while (taken.has(`${base}-${n}`)) n += 1;
      return `${base}-${n}`;
    },
    /** Create a minimal email template inline (title + a markdown body block)
     *  via the same endpoint the Templates editor uses, then refresh the picker
     *  and auto-select it. No navigation — the wizard's state survives. */
    async createTemplateInline() {
      const title = this.newTemplate.title.trim();
      if (!title || this.creatingTemplate) return;
      this.creatingTemplate = true; this.templateCreateError = null;
      try {
        const slug = this.templateSlug(title);
        const body = this.newTemplate.body.trim();
        const blocks = body ? [{ type: 'markdown', body }] : [];
        const res = await this.api.put(`/api/v1/branding/templates/email/${slug}`, { title, blocks });
        const p = res?.data ?? res;
        const id = p?.id ?? p?.data?.id;
        if (!id) throw new Error('Create returned no id.');
        await this.ensureTemplatesLoaded(true);
        this.newFlow.template_id = Number(id);
        this.showTemplateCreate = false;
        this.newTemplate = { title: '', body: '' };
      } catch (e) {
        this.templateCreateError = e?.response?.data?.error || e?.message || 'Could not create the template just now.';
      }
      this.creatingTemplate = false;
    },
    async loadSectorCatalogue() {
      try {
        const res = await this.api.get('/api/v1/admin/leads/sectors');
        const p = res?.data ?? res;
        const map = {};
        (Array.isArray(p?.sections) ? p.sections : []).forEach((s) => { map[s.section] = `${s.section} — ${s.section_label}`; });
        (Array.isArray(p?.codes) ? p.codes : []).forEach((c) => { map[c.code] = `${c.code} — ${c.label}`; });
        this.sectorLabels = map;
      } catch (e) { /* labels fall back to the raw token */ }
    },
    async loadRegions() {
      try {
        const res = await this.api.get('/api/v1/admin/leads/distinct/region');
        const p = res?.data ?? res;
        const list = p?.values ?? p?.data ?? p;
        this.regions = Array.isArray(list) ? list.filter(Boolean) : [];
      } catch (e) { /* datalist just stays empty */ }
    },
    buildStatsQuery() {
      const qs = new URLSearchParams();
      const a = this.audience;
      if (a.temperature) qs.set('temperature', a.temperature);
      if (a.stage) qs.set('stage', a.stage);
      const filters = [];
      if (a.sectors.length) filters.push({ field: 'sector', type: 'in', value: a.sectors.slice() });
      if (Number(a.min_employees) > 0) filters.push({ field: 'employee_count', type: '>=', value: Number(a.min_employees) });
      if (a.region.trim()) filters.push({ field: 'region', type: 'eq', value: a.region.trim() });
      if (a.company_status) filters.push({ field: 'company_status', type: 'eq', value: a.company_status });
      qs.set('filters', JSON.stringify(filters));
      return qs;
    },
    schedulePreview() {
      if (this.previewTimer) clearTimeout(this.previewTimer);
      this.previewTimer = setTimeout(() => this.refreshPreview(), 350);
    },
    async refreshPreview() {
      this.previewBusy = true; this.previewError = null;
      try {
        const res = await this.api.get('/api/v1/admin/leads/stats?' + this.buildStatsQuery().toString());
        const p = res?.data ?? res;
        this.previewCount = p?.stats?.total ?? 0;
      } catch (e) {
        this.previewError = 'Could not refresh the match count just now.';
      }
      this.previewBusy = false;
    },
    /** §5b — describe the audience → grounded targeting terms (one AI token). */
    async runSuggest() {
      const text = this.suggestText.trim();
      if (!text || this.suggestBusy) return;
      this.suggestBusy = true; this.suggestError = null;
      try {
        const res = await this.api.post('/api/v1/admin/leads/targeting-suggest', { text });
        this.suggestResult = res?.data ?? res;
      } catch (e) {
        // Surface the 4xx honestly (e.g. quota exceeded) — no native dialogs.
        // A 503 ai_unavailable becomes one calm sentence, never provider text.
        const d = e?.response?.data;
        let msg = apiErrorMessage(e, 'Could not get a suggestion just now.');
        if (!isAiUnavailable(e) && d?.tokens_consumed) msg += ` (${d.tokens_consumed} AI token${d.tokens_consumed === 1 ? '' : 's'} were still used.)`;
        this.suggestError = msg;
        this.aiDown = isAiUnavailable(e);
        this.suggestResult = null;
      }
      this.suggestBusy = false;
    },
    /** Fill the step-2 controls from the suggestion (this step only). */
    applySuggestion() {
      const r = this.suggestResult;
      if (!r) return;
      if (Array.isArray(r.sectors) && r.sectors.length) this.audience.sectors = r.sectors.slice();
      if (Array.isArray(r.region) && r.region.length) this.audience.region = String(r.region[0]);
      if (Number(r.min_employees) > 0) this.audience.min_employees = Number(r.min_employees);
      this.schedulePreview();
    },
    /** The Step-2 filters as one drip audience "source" object (blanks omitted). */
    audienceSource() {
      const a = this.audience;
      const o = {};
      if (a.temperature) o.temperature = a.temperature;
      if (a.stage) o.stage = a.stage;
      if (a.region.trim()) o.region = a.region.trim();
      if (Array.isArray(a.sectors) && a.sectors.length) o.sectors = a.sectors.slice();
      if (a.company_status) o.company_status = a.company_status;
      if (Number(a.min_employees) > 0) o.min_employees = Number(a.min_employees);
      return o;
    },
    goToReview() {
      if (!this.step3Ready) return;
      this.goTo(4);
    },
    /** Resolve the target flow id — creating a new draft flow or appending our
     *  audience onto an existing active one. Returns { id, created } or throws. */
    async resolveFlow() {
      const src = this.audienceSource();
      if (this.flowMode === 'new') {
        // Link the crawl source we picked/created so the new pipeline arrives with
        // its crawler attached (editable later on the Drip flows page).
        const linkedSourceId = this.sourceMode === 'existing'
          ? this.selectedSourceId
          : (this.createdSource && this.createdSource.id) || null;
        const body = {
          name: this.newFlow.name.trim(),
          trigger_type: 'manual',
          source_id: linkedSourceId || null,
          steps_json: [
            { type: 'send_email', template_id: Number(this.newFlow.template_id) },
            { type: 'wait', duration_seconds: (Number(this.newFlow.wait_value) || 1) * Number(this.newFlow.wait_unit) },
          ],
          audience_json: [src],
        };
        const res = await this.api.post('/api/v1/admin/drip-flows', body);
        const p = res?.data ?? res;
        const id = p?.flow?.id;
        if (!id) throw new Error('Flow create returned no id.');
        return { id, created: true };
      }
      // existing: append our audience source to the flow's current audience_json
      const id = this.selectedFlowId;
      const showRes = await this.api.get(`/api/v1/admin/drip-flows/${id}`);
      const sp = showRes?.data ?? showRes;
      const flow = sp?.flow ?? sp;
      const current = Array.isArray(flow?.audience_json) ? flow.audience_json : [];
      const merged = [...current, src];
      await this.api.put(`/api/v1/admin/drip-flows/${id}`, { audience_json: merged });
      return { id, created: false };
    },
    /** A short default name for a discovery pipeline when the user leaves it blank. */
    defaultDiscoveryName() {
      const base = this.hasNarrowedAudience
        ? this.audienceSummary
        : (this.resolvedSource ? (this.resolvedSource.name || 'crawl') : 'crawl');
      return `Discovery - ${base}`.slice(0, 120);
    },
    /** DISCOVERY MODE — create the pipeline in the 'discovering' state (launch)
     *  or as a plain draft (save). No template/steps: a discovery pipeline only
     *  needs a source + audience. The backend gates all sending on status, so a
     *  'discovering' flow never enrols or emails anyone. */
    async runDiscovery(launch) {
      const linkedSourceId = this.sourceMode === 'existing'
        ? this.selectedSourceId
        : (this.createdSource && this.createdSource.id) || null;
      const res = await this.api.post('/api/v1/admin/drip-flows', {
        name: (this.newFlow.name && this.newFlow.name.trim()) || this.defaultDiscoveryName(),
        trigger_type: 'manual',
        source_id: linkedSourceId || null,
        audience_json: [this.audienceSource()],
        discovery: !!launch,   // true → status 'discovering'; false → draft
      });
      const p = res?.data ?? res;
      const id = p?.flow?.id;
      if (!id) throw new Error('Create returned no id.');
      this.createdFlowId = id;
      this.createdFlowName = (this.newFlow.name && this.newFlow.name.trim()) || this.defaultDiscoveryName();
      this.launched = true;
      this.launchResult = launch
        ? 'Discovery pipeline live. Its crawl keeps running and matches accumulate under Prospects and Companies. Nobody is emailed. Turn on outreach later from the Drip flows page.'
        : 'Saved as a draft. It is not discovering yet - open it on the Drip flows page and switch it to Discovering to start gathering. Nobody is emailed.';
    },
    async run(launch) {
      this.launching = true; this.launchError = null; this.launchResult = null;
      try {
        if (this.pipelineMode === 'discovery') {
          await this.runDiscovery(launch);
          this.launching = false;
          return;
        }
        const { id, created } = await this.resolveFlow();
        this.createdFlowId = id;
        this.createdFlowName = this.flowMode === 'existing'
          ? (this.flows.find((x) => x.id === this.selectedFlowId)?.name || '')
          : (this.newFlow.name || '').trim();

        if (!launch) {
          this.launched = true;
          this.launchResult = created
            ? 'Saved. The new flow is a draft — activate it (or come back and Launch) to start enrolling. No emails have been sent.'
            : 'Saved. Your audience was added to the active flow; the audience sync cron will enrol matches within ~15 minutes. No emails have been sent.';
          this.launching = false;
          return;
        }

        // Launch: activate a newly-created flow, then run one audience sync.
        if (created) {
          await this.api.post(`/api/v1/admin/drip-flows/${id}/activate`, {});
        }
        const syncRes = await this.api.post('/api/v1/admin/drip-flows/audiences/sync', {});
        const sp = syncRes?.data ?? syncRes;
        const enrolled = sp?.enrolled ?? 0;
        this.launched = true;
        this.launchResult = `Pipeline live. ${created ? 'Flow activated. ' : ''}Audience sync enrolled ${enrolled} prospect(s). Emails send on the next runner tick (cron) — nothing was sent now.`;
      } catch (e) {
        this.launchError = e?.response?.data?.error || e?.message || 'Launch failed — nothing was activated.';
      }
      this.launching = false;
    },
  },
};
</script>

<style scoped>
.musubi-page--pipeline { padding: var(--m-space-6); max-width: 920px; }
.musubi-page__head h1 { font-weight: 750; }

/* AI prompt panel — teal identity (AI actions), never the indigo commit colour */
.pw-ai {
  position: relative; margin: 18px 0 8px; padding: 18px 20px;
  background: linear-gradient(180deg, color-mix(in srgb, var(--m-accent-2) 12%, #fff), var(--m-bg-card) 66%);
  border: 1px solid color-mix(in srgb, var(--m-accent-2) 32%, #fff); border-radius: var(--m-radius-lg);
}
.pw-ai__tag { position: absolute; top: 16px; right: 18px; background: var(--m-accent-2); color: #fff; font-weight: 700; font-size: 0.68rem; border-radius: 999px; padding: 3px 10px; }
.pw-ai__head h2 { font-size: 1.05rem; font-weight: 750; margin: 0; color: var(--m-text-primary); }
.pw-ai__head span { display: block; font-size: 0.82rem; color: var(--m-text-secondary); margin-top: 2px; }
.pw-ai__box {
  display: flex; align-items: flex-start; gap: 10px; margin: 12px 0 10px; padding: 10px 12px;
  background: var(--m-bg-card); border: 1.5px solid color-mix(in srgb, var(--m-accent-2) 34%, #fff); border-radius: var(--m-radius);
}
.pw-ai__spark { color: var(--m-accent-2); margin-top: 7px; }
.pw-ai__input { flex: 1; border: none; resize: vertical; font-size: 0.9rem; background: transparent; color: var(--m-text-primary); outline: none; padding: 4px 0; }
.pw-ai__go { flex: none; align-self: stretch; }
.btn-ai { background: var(--m-accent-2); color: #fff; border: 1px solid var(--m-accent-2); font-weight: 650; }
.btn-ai:hover:not(:disabled) { background: color-mix(in srgb, var(--m-accent-2) 82%, #000); color: #fff; }
.btn-ai:disabled { opacity: .55; cursor: not-allowed; }
.pw-ai__chips { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.pw-ai__try { font-size: 0.76rem; color: var(--m-text-tertiary); }
.pw-chip {
  display: inline-flex; align-items: center; gap: 6px; font-size: 0.78rem; cursor: pointer;
  color: var(--m-accent-2); background: color-mix(in srgb, var(--m-accent-2) 10%, #fff);
  border: 1px solid color-mix(in srgb, var(--m-accent-2) 30%, #fff); border-radius: 999px; padding: 4px 11px;
}
.pw-chip:hover:not(:disabled) { background: color-mix(in srgb, var(--m-accent-2) 18%, #fff); }
.pw-chip:disabled { opacity: .55; cursor: not-allowed; }
.pw-ai__cost { font-size: 0.74rem; color: var(--m-text-tertiary); margin: 10px 0 0; }
.pw-ai__cost i { color: var(--m-accent-2); }
.pw-ai__or { text-align: center; margin: 16px 0 0; border-top: 1px solid var(--m-border); }
.pw-ai__or span { position: relative; top: -10px; background: var(--m-bg-content); padding: 0 12px; font-size: 0.74rem; color: var(--m-text-tertiary); }

/* Stepper */
.pw-steps { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; margin: 18px 0 20px; }
.pw-step {
  display: flex; align-items: center; gap: 10px; text-align: left; cursor: pointer;
  background: var(--m-bg-card); border: 1px solid var(--m-border); border-radius: var(--m-radius-lg);
  box-shadow: var(--m-shadow-card); padding: 12px 14px; transition: all .12s;
}
.pw-step:disabled { cursor: not-allowed; opacity: .55; }
.pw-step.is-current { box-shadow: inset 0 0 0 1.5px var(--m-accent); border-color: var(--m-accent); }
.pw-step__num {
  flex: none; width: 26px; height: 26px; border-radius: 50%; display: grid; place-items: center;
  font-size: 0.8rem; font-weight: 700; background: var(--m-bg-content); color: var(--m-text-secondary);
}
.pw-step.is-current .pw-step__num { background: var(--m-accent); color: var(--m-accent-text); }
.pw-step.is-done .pw-step__num { background: var(--m-accent-soft); color: var(--m-accent); }
.pw-step__body { display: flex; flex-direction: column; line-height: 1.2; }
.pw-step__title { font-size: 0.83rem; font-weight: 650; color: var(--m-text-primary); }
.pw-step__sub { font-size: 0.7rem; color: var(--m-text-tertiary); }

/* Mode toggle */
.pw-mode { display: flex; gap: 10px; flex-wrap: wrap; }
.pw-mode__opt {
  display: flex; align-items: center; gap: 7px; cursor: pointer;
  border: 1px solid var(--m-border); border-radius: var(--m-radius); padding: 9px 13px;
  font-size: 0.85rem; font-weight: 600; color: var(--m-text-secondary);
}
.pw-mode__opt.is-on { border-color: var(--m-accent); background: var(--m-accent-soft); color: var(--m-accent); }

/* Pipeline mode choice (outreach vs. discovery) — stacked selectable cards */
.pw-modechoice { display: flex; flex-direction: column; gap: 10px; margin-bottom: 4px; }
.pw-modecard {
  display: flex; align-items: flex-start; gap: 12px; text-align: left; cursor: pointer;
  border: 1px solid var(--m-border); border-radius: var(--m-radius); background: var(--m-bg-card);
  padding: 13px 15px; transition: all .12s;
}
.pw-modecard:hover { border-color: var(--m-border-strong); }
.pw-modecard.is-on { border-color: var(--m-accent); background: var(--m-accent-soft); }
.pw-modecard__radio { color: var(--m-accent); font-size: 1rem; margin-top: 1px; }
.pw-modecard__body { display: flex; flex-direction: column; line-height: 1.3; }
.pw-modecard__title { font-weight: 650; color: var(--m-text-primary); font-size: 0.9rem; }
.pw-modecard__sub { font-size: 0.78rem; color: var(--m-text-tertiary); margin-top: 2px; }

/* Vertical entry point — "what kind of business?" above the crawler tiles */
.pw-vertical {
  border: 1px solid var(--m-border); border-left: 3px solid var(--m-accent);
  border-radius: var(--m-radius); background: var(--m-bg-content);
  padding: 13px 15px; margin-bottom: 16px;
}
.pw-vertical__h { font-weight: 650; font-size: 0.9rem; color: var(--m-text-primary); margin-bottom: 8px; }
.pw-vertical__in { max-width: 420px; }
.pw-vertical__list {
  display: flex; flex-wrap: wrap; gap: 7px; margin-top: 10px;
  max-height: 170px; overflow-y: auto; align-content: flex-start; padding: 2px;
}
.pw-vertical__opt {
  display: inline-flex; align-items: center; gap: 7px; cursor: pointer;
  border: 1px solid var(--m-border); border-radius: 999px; background: var(--m-bg-card);
  padding: 4px 12px; font-size: 0.78rem; font-weight: 600; color: var(--m-text-secondary);
}
.pw-vertical__opt:hover { border-color: var(--m-border-strong); }
.pw-vertical__opt.is-on { border-color: var(--m-accent); background: var(--m-accent-soft); color: var(--m-accent); }
.pw-vertical__cov {
  font-size: 0.62rem; font-weight: 700; text-transform: uppercase; letter-spacing: .03em;
  color: var(--m-text-tertiary);
}
.pw-vertical__cov--places { color: var(--m-status-warning); }
.pw-vertical__note {
  margin: 10px 0 0; font-size: 0.8rem; line-height: 1.45; color: var(--m-text-secondary);
  background: var(--m-accent-soft); border-radius: 6px; padding: 8px 11px;
}
.pw-vertical__hint { margin: 8px 0 0; font-size: 0.76rem; color: var(--m-text-tertiary); }

/* Discovery "what will happen" bullets */
.pw-bullets { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 9px; }
.pw-bullets li { display: flex; align-items: flex-start; gap: 10px; font-size: 0.85rem; color: var(--m-text-secondary); }
.pw-bullets li i { color: var(--m-accent); margin-top: 2px; }

.pw-empty { color: var(--m-text-secondary); font-size: 0.88rem; background: var(--m-bg-content); border: 1px dashed var(--m-border-strong); border-radius: var(--m-radius); padding: 14px; }

/* Selectable rows */
.pw-srclist { display: flex; flex-direction: column; gap: 8px; }
.pw-srcrow {
  display: flex; align-items: center; gap: 12px; text-align: left; cursor: pointer;
  border: 1px solid var(--m-border); border-radius: var(--m-radius); background: var(--m-bg-card);
  padding: 11px 13px; transition: all .12s;
}
.pw-srcrow:hover { border-color: var(--m-border-strong); }
.pw-srcrow.is-on { border-color: var(--m-accent); background: var(--m-accent-soft); }
.pw-srcrow__radio { color: var(--m-accent); font-size: 1rem; }
.pw-srcrow__body { display: flex; flex-direction: column; line-height: 1.25; margin-right: auto; }
.pw-srcrow__name { font-weight: 650; color: var(--m-text-primary); font-size: 0.9rem; }
.pw-srcrow__meta { font-size: 0.74rem; color: var(--m-text-tertiary); }

/* Suggest targeting (teal AI box, §5b) */
.pw-suggest {
  background: var(--m-accent-2-soft, color-mix(in srgb, var(--m-accent-2) 10%, #fff));
  border: 1px solid color-mix(in srgb, var(--m-accent-2) 32%, #fff);
  border-radius: var(--m-radius); padding: 12px 13px;
}
.pw-suggest__h {
  display: flex; align-items: center; gap: 7px; font-size: 0.7rem; font-weight: 800;
  letter-spacing: 0.06em; text-transform: uppercase; color: var(--m-accent-2); margin-bottom: 9px;
}
.pw-suggest__in { display: flex; gap: 8px; align-items: stretch; }
.pw-suggest__fld {
  flex: 1; background: var(--m-bg-card); border: 1px solid color-mix(in srgb, var(--m-accent-2) 34%, #fff);
  border-radius: 8px; padding: 8px 11px; font-size: 0.85rem; color: var(--m-text-primary); outline: none;
}
.pw-suggest__go {
  flex: none; display: inline-flex; align-items: center; border-radius: 8px;
  padding: 7px 14px; font-size: 0.82rem; cursor: pointer;
}
.pw-suggest__go:disabled { opacity: .55; cursor: not-allowed; }
.pw-suggest__meta { font-size: 0.72rem; color: var(--m-text-tertiary); margin: 8px 0 0; }
.pw-suggest__meta i { color: var(--m-accent-2); }
.pw-suggest__out {
  margin-top: 10px; background: var(--m-bg-card);
  border: 1px solid color-mix(in srgb, var(--m-accent-2) 22%, #fff); border-radius: 8px; padding: 10px 11px;
}
.pw-suggest__ol {
  display: flex; align-items: center; gap: 6px; font-size: 0.66rem; font-weight: 700;
  text-transform: uppercase; letter-spacing: 0.05em; color: var(--m-accent-2); margin-bottom: 8px;
}
.pw-suggest__chips { display: flex; flex-wrap: wrap; gap: 6px; }
.pw-suggest__chip {
  display: inline-flex; align-items: center; gap: 6px; font-size: 0.76rem; font-weight: 600;
  color: var(--m-text-secondary); background: var(--m-bg-content);
  border: 1px solid var(--m-border); border-radius: 999px; padding: 3px 10px;
}
.pw-suggest__chip--geo { color: var(--m-accent-2); background: color-mix(in srgb, var(--m-accent-2) 10%, #fff); border-color: color-mix(in srgb, var(--m-accent-2) 28%, #fff); }
.pw-suggest__code { font-family: var(--m-font-mono); font-weight: 700; font-size: 0.72rem; color: var(--m-accent-2); }
.pw-suggest__none { font-size: 0.78rem; color: var(--m-text-secondary); margin: 0; }
.pw-suggest__foot { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-top: 10px; }
.pw-suggest__est { font-size: 0.78rem; font-weight: 650; color: var(--m-text-secondary); }

/* Audience */
.pw-aud { display: flex; flex-direction: column; gap: 14px; }
.pw-aud__row { display: flex; flex-direction: column; gap: 5px; }
.pw-aud__ctrl { max-width: 480px; }
.pw-aud__grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 14px; }
.pw-aud__hint { font-size: 0.7rem; color: var(--m-text-tertiary); margin: 4px 0 0; line-height: 1.35; }
.pw-aud__lbl { font-size: 0.72rem; text-transform: uppercase; letter-spacing: 0.04em; font-weight: 650; color: var(--m-text-secondary); display: block; margin-bottom: 4px; }
.pw-aud__note { display: block; font-size: 0.72rem; color: var(--m-text-tertiary); margin-top: 3px; }
.pw-advanced summary { cursor: pointer; font-size: 0.78rem; font-weight: 600; color: var(--m-text-secondary); }
.pw-wait { display: flex; gap: 8px; }
.pw-wait .form-control { max-width: 90px; }

/* Template picker + inline creator (step 3, new-flow path) */
.pw-tplpick { display: flex; gap: 8px; align-items: stretch; }
.pw-tplpick select { flex: 1; }
.pw-tplpick__new { flex: none; white-space: nowrap; }
.pw-tplcreate {
  margin-top: 6px; padding: 13px 14px;
  background: var(--m-bg-content); border: 1px solid var(--m-border); border-radius: var(--m-radius);
}
.pw-tplcreate__h {
  display: flex; align-items: center; font-size: 0.82rem; font-weight: 700;
  color: var(--m-text-primary); margin-bottom: 8px;
}
.pw-tplcreate__h i { color: var(--m-accent); }
.pw-tplcreate__empty { font-size: 0.82rem; color: var(--m-text-secondary); margin: 0 0 10px; }
.pw-tplcreate__foot {
  display: flex; align-items: center; justify-content: space-between; gap: 12px;
  margin-top: 12px; flex-wrap: wrap;
}
.pw-tplcreate__link {
  display: inline-flex; align-items: center; font-size: 0.78rem; font-weight: 600;
  color: var(--m-text-secondary); text-decoration: none;
}
.pw-tplcreate__link:hover { color: var(--m-accent); text-decoration: underline; }
.pw-tplcreate__actions { display: flex; align-items: center; gap: 6px; margin-left: auto; }

.pw-preview {
  display: flex; align-items: center; gap: 10px; margin-top: 4px;
  background: var(--m-accent-soft); border: 1px solid var(--m-accent); border-radius: var(--m-radius);
  padding: 12px 16px; color: var(--m-accent);
}
.pw-preview.is-empty { background: var(--m-bg-content); border-color: var(--m-border); color: var(--m-text-secondary); }
.pw-preview i { font-size: 1.1rem; }
.pw-preview__n { font-size: var(--m-text-xl); font-weight: 750; }
.pw-preview__t { font-size: 0.85rem; color: var(--m-text-secondary); }

/* Review */
.pw-summary { display: flex; align-items: center; gap: 14px; background: var(--m-bg-content); border-radius: var(--m-radius-lg); padding: 16px 18px; margin-bottom: 16px; }
.pw-summary__icon { font-size: 1.5rem; color: var(--m-accent); }
.pw-summary__text { margin: 0; font-size: 1rem; font-weight: 600; color: var(--m-text-primary); line-height: 1.5; }
.pw-review { list-style: none; margin: 0 0 8px; padding: 0; }
.pw-review li { display: flex; gap: 16px; padding: 11px 4px; border-bottom: 1px solid var(--m-border); }
.pw-review li:last-child { border-bottom: none; }
.pw-review__k { flex: none; width: 120px; display: flex; align-items: center; gap: 8px; font-size: 0.78rem; font-weight: 650; color: var(--m-text-secondary); text-transform: uppercase; letter-spacing: 0.03em; }
.pw-review__k i { color: var(--m-accent); }
.pw-review__v { font-size: 0.9rem; color: var(--m-text-primary); }

/* Nav */
.pw-nav { display: flex; justify-content: space-between; align-items: center; margin-top: 18px; padding-top: 14px; border-top: 1px solid var(--m-border); }
.pw-nav--launch .pw-nav__right { display: flex; gap: 10px; }
.pw-hint { color: var(--m-text-tertiary); }

/* Finish step — success card that deep-links to the created pipeline. */
.pw-finish {
  border: 1px solid color-mix(in srgb, var(--m-accent) 30%, #fff);
  background: linear-gradient(180deg, var(--m-accent-soft), var(--m-bg-card) 70%);
  border-radius: var(--m-radius-lg); padding: 16px 18px;
}
.pw-finish__head { display: flex; align-items: flex-start; gap: 12px; }
.pw-finish__ic { font-size: 1.4rem; color: var(--m-accent); margin-top: 1px; flex: none; }
.pw-finish__copy { display: flex; flex-direction: column; line-height: 1.4; }
.pw-finish__title { font-size: 1rem; font-weight: 700; color: var(--m-text-primary); }
.pw-finish__sub { font-size: 0.85rem; color: var(--m-text-secondary); margin-top: 3px; }
.pw-finish__actions { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; margin-top: 14px; }

@media (max-width: 720px) {
  .pw-steps { grid-template-columns: repeat(2, 1fr); }
  .pw-aud__grid { grid-template-columns: 1fr; }
}
</style>
